/**
 * LeadExplorerHost — monta o LeadExplorerDialog quando o store pede (lazy: o módulo
 * carrega tlds + chart.js só quando o usuário abre a ferramenta).
 * Movimento (23/10/2026): a janela fica montada enquanto sai (.ds-dialogo.ds-grande: entra em
 * 280 ms, sai em 200 ms), com o mesmo pedido de quando estava aberta, e o foco volta ao gatilho.
 */
import { Suspense, lazy, useEffect, useState } from 'react';
import { useLeadExplorerStore } from './leadExplorerStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useCtaStore } from '../account/ctaStore';
import { level } from '../../lib/entitlements';
import { usePresenca } from '../../hooks/usePresenca';
import { useDevolverFoco } from './movimentoLx';

const LeadExplorerDialog = lazy(() =>
  import('./LeadExplorerDialog').then((m) => ({ default: m.LeadExplorerDialog })),
);

export function LeadExplorerHost(): JSX.Element | null {
  const open = useLeadExplorerStore((s) => s.open);
  const term = useLeadExplorerStore((s) => s.term);
  const run = useLeadExplorerStore((s) => s.run);
  const savedId = useLeadExplorerStore((s) => s.savedId);
  const closeExplorer = useLeadExplorerStore((s) => s.closeExplorer);
  const session = useSessionStore((s) => s.session);
  const openCTA = useCtaStore((s) => s.openCTA);
  // O dossiê completo é conferido ao pesquisar (`explorer.mode`). Reabrir uma análise guardada
  // vale também com a licença vencida (decisão 144).
  const allowed = Boolean(savedId) || level(session, 'explorer.mode') !== 'off';
  const visible = open && allowed;
  const { montado, aberto } = usePresenca(visible, '--duracao-200');
  // Ao fechar (inclusive do resultado), o foco volta ao botão que abriu o LeadExplorer.
  useDevolverFoco(visible);
  // O pedido de quando estava aberta: ao fechar, o store limpa term e savedId, mas a janela que
  // sai continua a mesma (a chave não pode mudar no meio da saída).
  const [request, setRequest] = useState({ term, run, savedId });
  if (visible && (request.term !== term || request.run !== run || request.savedId !== savedId)) {
    setRequest({ term, run, savedId });
  }

  useEffect(() => {
    if (!open || allowed) return;
    closeExplorer();
    openCTA('lead-explorer');
  }, [allowed, closeExplorer, open, openCTA]);

  if (!montado) return null;
  const shown = visible ? { term, run, savedId } : request;
  return (
    <Suspense fallback={null}>
      <LeadExplorerDialog
        key={shown.savedId ?? shown.term ?? 'new'}
        initialTerm={shown.term}
        autoRun={shown.run}
        savedId={shown.savedId}
        aberto={aberto}
        onClose={closeExplorer}
      />
    </Suspense>
  );
}
